/** @file clip.import: adds (or with `replace` overwrites) a clip from a whole validated clip document. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import { validateClip } from '@zilo/pose3d-format/validate/validateClip';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { isId } from '@zilo/pose3d-std/ids/isId';
import type { CommandDef } from '../apply/commandDef';
import { clipInverse } from '../apply/clipInverse';
import type { CommandContext } from '../context/commandContext';
import type { PuppetDoc } from '../puppetDoc/puppetDoc';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { withPathPrefix } from '../validate/withPathPrefix';
import { unknownNodeWarnings } from './unknownNodeWarnings';

/** The validated clip; its id is a fresh one when `doc.id` is not a `clp` id. */
function readClip(doc: unknown, ctx: CommandContext): Clip {
  const copy = structuredClone(doc);
  if (typeof copy === 'object' && copy !== null && !Array.isArray(copy) && !isId((copy as { id?: unknown }).id, 'clp')) {
    (copy as { id: string }).id = ctx.makeId('clp');
  }
  return withPathPrefix('/input/doc', () => validateClip(copy));
}

function idTaken(draft: PuppetDoc, id: string, except: string): boolean {
  return Object.entries(draft.clips).some(([name, c]) => name !== except && c.id === id);
}

/**
 * `clip.import { doc, name?, replace? }` → `{ id, name }`: the clip is stored under `name ?? doc.name`, keeping
 * `doc.id` unless another clip uses it. Inverse: delete it, or re-import the replaced clip.
 */
export const clipImportCommand: CommandDef<'clip.import'> = {
  name: 'clip.import',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['doc', 'name', 'replace']);
    return compact({ doc: r.raw('doc'), name: r.opt('name', r.str), replace: r.opt('replace', r.bool) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const clip = readClip(input.doc, ctx);
    const name = input.name ?? clip.name;
    const prev = draft.clips[name];
    if (prev !== undefined && input.replace !== true) {
      throwError(ERROR_CODES.VALIDATION, { clip: name }, { path: '/input/name', hint: 'pass replace: true, or clip.rename / clip.delete the existing clip' });
    }
    if (idTaken(draft, clip.id, name)) clip.id = ctx.makeId('clp');
    clip.name = name;
    const inverse = prev === undefined ? [{ name: 'clip.delete' as const, input: { clip: name } }] : [clipInverse(prev)];
    draft.clips[name] = clip;
    return { result: { id: clip.id, name }, warnings: unknownNodeWarnings(draft.puppet, name, clip), inverse };
  },
};
